/**
 * File: SideNavigationItem.js
 * Origin: isotect
 * Description: SidebarItem component to render various sidebar elements.
 *
 * Version: v1.0.0
 * Created: 2026-04-22
 * Last Updated: 2026-04-24
 *
 * Used By: isotect
 * Maintainer: Zachary Dicks
 *
 * Notes:
 * - Changes in this file may affect all consuming frontend modules.
 * - See Git repository 'isotect' for full change history.
 */

// Third-party packages
import { Link } from "react-router-dom";
import { useState } from "react";

/**
 * SidebarItem component for rendering a sidebar navigation item.
 * Supports both link and button variants.
 *
 * @component
 *
 * @param {Object} props - Component props
 * @param {'link' | 'button'} props.type - Type of sidebar item
 * @param {React.ElementType} props.icon - Icon component to render
 * @param {string} [props.label] - Visible label for the item
 * @param {string} [props.tooltip] - Tooltip text shown when sidebar or component is collapsed
 * @param {string} [props.linkHref] - Destination URL (required if type is "link")
 * @param {Function} [props.onClick] - Click handler (required if type is "button")
 * @param {boolean} props.isCollapsed - Whether the sidebar is in collapsed state
 * @param {number} [props.iconSize] - Size of the icon in pixels
 * @param {string} [props.className] - Additional CSS class names
 *
 * @returns {JSX.Element} Rendered sidebar item (link or button)
 */
function SidebarItem({
    type,
    icon: Icon,
    label,
    tooltip,
    linkHref,
    onClick,
    isCollapsed,
    iconSize = 24,
    className = "",
}) {
    const [isTooltipVisible, setIsTooltipVisible] = useState(false);

    const isLink = type === "link";
    const isButton = type === "button";

    if (!isLink && !isButton) return null;

    const Component = isLink ? Link : "button";

    const componentProps = isLink
        ? { to: linkHref }
        : { type: "button", onClick };

    const handleMouseEnter = () => {
        if (isCollapsed || !label) setIsTooltipVisible(true);
    };

    const handleMouseLeave = () => {
        setIsTooltipVisible(false);
    };

    return (
        <>
            <div
                className={`sidebar-item ${className}`}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
            >
                <Component
                    className="sidebar-item__component"
                    {...componentProps}
                >
                    <Icon size={iconSize} />
                    {!isCollapsed && label && (
                        <p className="sidebar-item__label">{label}</p>
                    )}
                </Component>
                {isTooltipVisible && (
                    <p className="sidebar-item__tooltip">{tooltip}</p>
                )}
            </div>
        </>
    );
}

export default SidebarItem;
